import {
  Center,
  CircularProgress,
  CircularProgressLabel,
  DotLoader,
  SimpleGrid,
  Text,
  VStack,
} from '@wegroup/design-system';
import React, { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ProductWithQuality } from '../../../../../../../../types/Quotes';
import LoadingScreen from '../../../../../../../common/src/components/LoadingScreen';
import { RootState } from '../../../../../store/redux/reducers/rootReducer';
import {
  setLoadedProductIds,
  setShownProducts,
  setShownProductsOrder,
} from '../../../../../store/redux/slices/offersSlice';
import { ProductInfoAndContent } from '../../../api/getProducts';
import useIsMultiQuoteContractFlow from '../../../hooks/useIsMultiQuoteContractFlow';
import useOffersPageParams from '../../../hooks/useOffersPageParams';
import useOffersSortedProducts from '../../../hooks/useOffersSortedProducts';
import { EnabledOfferActions } from '../../offer/offer-actions/OfferActions';
import SingleOffer from '../../offer/SingleOffer';
import OffersNotFound from './OffersNotFound';

interface Props {
  products: ProductWithQuality[];
  productContentItems: ProductInfoAndContent[];
  isLoading: boolean;
}

/**
 * This component has hardcoded data for the PIA boat flow (DNA)
 * The PIA boat flow will only show the products when all quotes are loaded
 * In the meanwhile it will show a progress circle
 */
const NewOffersOverviewContent: React.FC<Props> = ({
  products,
  productContentItems,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { riskObject } = useOffersPageParams();
  const dispatch = useDispatch();
  const sortedProducts = useOffersSortedProducts(products) || [];
  const isMultiQuoteSelect = useIsMultiQuoteContractFlow();
  const enabledActions: EnabledOfferActions[] = [];

  const loadedProductIds =
    useSelector((state: RootState) => state.offers.loadedProductIds) || [];
  const shownProducts = useSelector(
    (state: RootState) => state.offers.shownProducts,
  );

  const showProgressLoader =
    loadedProductIds.length < sortedProducts.length && riskObject === 'boat';

  useLayoutEffect(() => {
    /** When this component loads, the shown offers should reset */
    dispatch(setShownProductsOrder(undefined));
    dispatch(setShownProducts([]));
    dispatch(setLoadedProductIds([]));
  }, []);

  // Compare is only needed when there are multiple products
  if (products.length > 1 && products.some(({ quality }) => !!quality))
    enabledActions.push('COMPARE');

  return (
    <>
      {showProgressLoader && (
        <Center my="auto">
          <VStack spacing="2">
            <Text textStyle="largeBodyMedium">{t('Calculating quotes')}</Text>
            <CircularProgress
              value={(loadedProductIds.length / sortedProducts.length) * 100}
              size="32"
              color="primary.500"
            >
              <CircularProgressLabel>
                <Center>
                  <DotLoader />
                </Center>
              </CircularProgressLabel>
            </CircularProgress>
          </VStack>
        </Center>
      )}
      <SimpleGrid w="100%" gridGap={{ base: 4, laptop: 12 }}>
        {products.length > 0 ? (
          <>
            {sortedProducts.map((product) => (
              <SingleOffer
                product={product}
                isHidden={showProgressLoader}
                key={product.hash_id}
                enabledActions={enabledActions}
                productContentItems={productContentItems}
                isSelectable={products.length > 1 && isMultiQuoteSelect}
              />
            ))}
            {!showProgressLoader && !shownProducts.length && <OffersNotFound />}
            {isLoading && <LoadingScreen />}
          </>
        ) : (
          <OffersNotFound />
        )}
      </SimpleGrid>
    </>
  );
};

export default NewOffersOverviewContent;
